import {Box, Button, Typography} from '@mui/material';
import {QueryBuilderDnD} from '@react-querybuilder/dnd';
import {t} from 'i18next';
import {isEmpty} from 'lodash';
import {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as ReactDnD from 'react-dnd';
import {useDrop} from 'react-dnd';
import * as ReactDndHtml5Backend from 'react-dnd-html5-backend';
import {QueryBuilder, RuleGroupTypeIC, RuleType} from 'react-querybuilder';
import 'react-querybuilder/dist/query-builder.scss'; // recommended
import {useDispatch, useSelector} from 'react-redux';
import {v4 as uuidv4} from 'uuid';
import {ReactComponent as AddIcon} from '../../../assests/svg/plus-icon-success.svg';
import {ReactComponent as SyntaxIcon} from '../../../assests/svg/syntax.svg';
import {useWindowSize} from '../../../hooks/utils/useWindowSize';
import {
  setEditField,
  setHasTransferListById,
  setSavedSearchById,
  setSavedSearchRelationalGroupById,
  updateQuery,
} from '../../../redux/slices/querySlice';
import {RootState} from '../../../redux/store';
import {
  getDateAndTimeOperators,
  getFileTransferListOperators,
  getGeneralOperators,
  getKeywordOperators,
  getNumberOperators,
  getTextOperators,
  QBCombinators,
  QBDropTypes,
  QBOperators,
} from '../../../utils/queryBuilder';
import AurButton from '../../common/aur-button/AurButton';
import AurCustomCombinatorSelector from '../../common/aur-custom-combinator-selector/AurCustomCombinatorSelector';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import ECASavedSearchPanelSideDrawer, {
  SavedSearchType,
} from '../../eca/ECASavedSearchSidePanelDrawer';
import {
  FieldType,
  FieldUIType,
  FieldUITypeMappingObj,
} from '../field-management/custom-field/constant';
import CustomGroup from './CustomGroup';
import {CustomDragHandle, CustomRule} from './CustomRule';
import {ViewSyntaxDrawer} from './ViewSyntaxDrawer';
import {QueryFieldDefaultDrawer} from './query-field/QueryFieldDefaultDrawer';
import {QueryFieldExportDrawer} from './query-field/QueryFieldExportDrawer';
import {
  IQueryFieldMetadataDrawerValue,
  QueryFieldMetadataDrawer,
} from './query-field/QueryFieldMetadataDrawer';
import {QueryFieldTagDrawer} from './query-field/QueryFieldTagDrawer';
import {processRules, setCustomRule} from './useCustomRule';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';

interface QueryBuilderComponentProps {
  onPressAddRelationshipGroup: () => void;
  selectedRelationshipGroupArray?: string[];
}
interface QBField {
  name: string;
  label: string;
  operators?: {
    name: string;
    label: string;
  }[];
}

export interface QueryBuilderComponentPropsRef {
  openOnDropItem: (type: string) => void;
}

const QueryBuilderComponent = forwardRef(
  (
    props: QueryBuilderComponentProps,
    ref: Ref<QueryBuilderComponentPropsRef>
  ) => {
    const {onPressAddRelationshipGroup, selectedRelationshipGroupArray = []} =
      props;

    const typographyRef = useRef<HTMLDivElement>(null);
    const [hasRelationshipGroupMoreCount, setHasRelationshipGroupMoreCount] =
      useState<boolean>(false);

    const windowSize = useWindowSize();
    useEffect(() => {
      if (typographyRef.current) {
        const {scrollWidth, clientWidth} = typographyRef.current;
        setHasRelationshipGroupMoreCount(scrollWidth > clientWidth);
      }
    }, [windowSize, selectedRelationshipGroupArray]);

    const selectedRelationshipGroupText = useMemo(
      () => selectedRelationshipGroupArray.join(', '),
      [selectedRelationshipGroupArray]
    );
    const initialFields: QBField[] = [];
    const [fields, setFields] = useState<QBField[]>(initialFields);

    const dispatch = useDispatch();
    const {trackGAEvent} = useAnalytics();
    const {builder, editField} = useSelector((state: RootState) => state.query);

    const [isAtTop, setIsAtTop] = useState(true);
    const [viewSyntax, setViewSyntax] = useState(false);
    const [isShowTagPanel, setIsShowTagPanel] = useState(false);
    const [isShowExportPanel, setIsShowExportPanel] = useState(false);
    const [isShowStandardMetadataPanel, setIsShowStandardMetadataPanel] =
      useState(false);
    const [isShowCustomMetadataPanel, setIsShowCustomMetadataPanel] =
      useState(false);
    const [isSavedSearchDialogVisible, setIsSavedSearchDialogVisible] =
      useState(false);
    const [isShowDefaultFieldPanel, setIsShowDefaultFieldPanel] =
      useState(false);

    // Drop handling
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, drop] = useDrop(
      () => ({
        accept: [
          QBDropTypes.DEFAULT_FIELD,
          QBDropTypes.STANDARED_METADATA_FIELD,
          QBDropTypes.CUSTOM_METADATA_FIELD,
          QBDropTypes.SAVED_SEARCH,
          QBDropTypes.TAG,
          QBDropTypes.EXPORT,
          QBDropTypes.GROUP,
        ],
        drop: (
          item: {field: {name: string; label: string}; type: string},
          monitor
        ) => {
          if (monitor.didDrop()) {
            return;
          }
          if (item.type != QBDropTypes.GROUP) {
            switch (item.type) {
              case QBDropTypes.DEFAULT_FIELD:
                setIsShowDefaultFieldPanel(true);
                break;
              case QBDropTypes.STANDARED_METADATA_FIELD:
                setIsShowStandardMetadataPanel(true);
                break;
              case QBDropTypes.CUSTOM_METADATA_FIELD:
                setIsShowCustomMetadataPanel(true);
                break;
              case QBDropTypes.TAG:
                setIsShowTagPanel(true);
                break;
              case QBDropTypes.SAVED_SEARCH:
                setIsSavedSearchDialogVisible(true);
                break;
              case QBDropTypes.EXPORT:
                setIsShowExportPanel(true);
                break;
            }
            trackComponentOnDrag(item.field.label);
          } else {
            addRuleGroup();
          }
        },
        collect: (monitor) => ({
          isOver: !!monitor.isOver(),
        }),
      }),
      [builder]
    );

    useImperativeHandle(ref, () => {
      return {
        openOnDropItem(type: string) {
          switch (type) {
            case QBDropTypes.DEFAULT_FIELD:
              setIsShowDefaultFieldPanel(true);
              break;
            case QBDropTypes.STANDARED_METADATA_FIELD:
              setIsShowStandardMetadataPanel(true);
              break;
            case QBDropTypes.CUSTOM_METADATA_FIELD:
              setIsShowCustomMetadataPanel(true);
              break;
            case QBDropTypes.TAG:
              setIsShowTagPanel(true);
              break;
            case QBDropTypes.SAVED_SEARCH:
              setIsSavedSearchDialogVisible(true);
              break;
            case QBDropTypes.EXPORT:
              setIsShowExportPanel(true);
              break;
          }
        },
      };
    }, []);

    const clearEditField = () => {
      dispatch(setEditField(undefined));
    };

    const getOperatorList = (values: any) => {
      let operators = initialFields;

      if (values?.hasTransferList ?? false) {
        operators = getFileTransferListOperators(values.fieldType as FieldType);
      } else {
        switch (values.fieldType) {
          case 'Keyword':
            operators = getKeywordOperators();
            break;
          case 'ExtractedText':
          case 'TextMultivalue':
          case 'Text':
            operators = getTextOperators();
            break;
          case 'Float':
          case 'Number':
            operators = getNumberOperators();
            break;
          case 'Date':
          case 'DateTime':
          case 'Time':
            operators = getDateAndTimeOperators();
            break;
          case 'tag':
          case 'saved_search':
          case 'export':
            operators = getGeneralOperators();
            break;
        }
      }
      return operators;
    };

    const addQuery = ({
      fieldName,
      fieldLabel,
      valueLabel,
      values,
      extraData,
    }: {
      fieldName: string;
      fieldLabel: string;
      valueLabel?: string;
      values: any;
      extraData: {
        operator: QBOperators;
        fieldType?:
          | FieldType
          | 'tag'
          | 'export'
          | 'saved_search'
          | 'default_field';
      };
    }) => {
      const newRule: RuleType<string, string, any, string> = {
        field: fieldName,
        operator: extraData?.operator ?? QBOperators.anyOfThese,
        ...values,
      };
      const getOperators = getOperatorList(values);

      const checkfieldExits = fields.find((value) => value.name === fieldName);
      if (isEmpty(checkfieldExits)) {
        setFields([
          ...fields,
          {
            name: fieldName,
            label: fieldLabel,
            operators: getOperators,
          },
        ]);
      }
      const updateValueLabel = valueLabel ?? values.value;
      setCustomRule(values.id, {
        valueLabel: values?.hasTransferList
          ? updateValueLabel.replaceAll(' OR ', '; ')
          : updateValueLabel,
      });

      if (editField) {
        const newQuery = JSON.parse(JSON.stringify(builder));
        let target = newQuery;

        for (const index of editField.path) {
          target = target.rules[index];
        }

        if (target.id === editField.id) {
          Object.assign(target, newRule);
        }
        dispatch(updateQuery(newQuery));
        clearEditField();
      } else {
        // Create a new queryBuilder object to avoid direct mutation
        const queryBuilder = {
          ...builder, // Spread the existing builder
          rules: builder.rules.length
            ? [...builder.rules, QBCombinators.AND, newRule]
            : [...builder.rules, newRule], // Add new rule to the existing rules array
        } as RuleGroupTypeIC;

        // Dispatch the updated query to the store
        dispatch(updateQuery(queryBuilder));
      }
    };

    // Control elements including the custom rule component
    const controlElements = {
      combinatorSelector: (props: any) => (
        <AurCustomCombinatorSelector
          {...props}
          options={[
            {label: t('queryBuilder.and'), name: QBCombinators.AND},
            {label: t('queryBuilder.or'), name: QBCombinators.OR},
            // {label: t('queryBuilder.not'), name: QBCombinators.NOT},
          ]}
        />
      ),
      addRuleAction: () => null,
      addGroupAction: () => null,
      rule: CustomRule, // Use the custom rule renderer
      ruleGroup: CustomGroup,
      dragHandle: CustomDragHandle,
    };

    useEffect(() => {
      const handleScroll = () => {
        setIsAtTop(window.pageYOffset === 0);
      };

      window.addEventListener('scroll', handleScroll);
      handleScroll();

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }, []);

    type AnyRule = RuleType | RuleGroupTypeIC;

    const extractRules = (rules: AnyRule[]): RuleType[] => {
      const result: RuleType[] = [];

      rules.forEach((rule) => {
        // RuleGroup (nested)
        if (typeof rule === 'string') return;

        if ('rules' in rule) {
          result.push(...extractRules(rule.rules as AnyRule[]));
        }
        // Actual rule
        else if ('field' in rule) {
          result.push(rule);
        }
      });

      return result;
    };

    useEffect(() => {
      if (!builder?.rules?.length) return;

      const flatRules = extractRules(builder.rules as AnyRule[]);

      const nextFields: QBField[] = [];

      flatRules.forEach((rule: any) => {
        const name = rule.field;
        const label = (rule as any).ruleLabel ?? rule.field;

        // prevent duplicates
        if (nextFields.some((f) => f.name === name)) return;
        const operators = getOperatorList({
          hasTransferList: rule.hasTransferList,
          fieldType: rule.fieldType,
        });

        nextFields.push({
          name,
          label,
          operators,
        });

        const updateValueLabel = rule.valueLabel ?? rule.value;
        setCustomRule(rule.id, {
          valueLabel: rule?.hasTransferList
            ? updateValueLabel.replaceAll(' OR ', '; ')
            : updateValueLabel,
        });
      });

      setFields(nextFields);
    }, [builder]);

    const onEdit = useCallback(() => {
      const metaDataList = editField?.id.split('__');
      const dropType = metaDataList?.[1];
      switch (dropType) {
        case QBDropTypes.DEFAULT_FIELD:
          setIsShowDefaultFieldPanel(true);
          break;
        case QBDropTypes.STANDARED_METADATA_FIELD:
          setIsShowStandardMetadataPanel(true);
          break;
        case QBDropTypes.CUSTOM_METADATA_FIELD:
          setIsShowCustomMetadataPanel(true);
          break;
        case QBDropTypes.TAG:
          setIsShowTagPanel(true);
          break;
        case QBDropTypes.SAVED_SEARCH:
          setIsSavedSearchDialogVisible(true);
          break;
        case QBDropTypes.EXPORT:
          setIsShowExportPanel(true);
          break;
      }
    }, [editField?.id]);

    const initialEditValue = useMemo(() => {
      if (editField?.id) {
        const metaDataList = editField?.id.split('__');
        return {
          fieldId: metaDataList?.at(-1),
          value: editField.value,
          operator: editField.operator,
          hasTransferList: editField?.hasTransferList ?? false,
        };
      }
      return undefined;
    }, [editField]);

    useEffect(() => {
      if (editField) {
        onEdit();
      }
    }, [editField, onEdit]);

    const handleDefaultFieldApply = (value?: string) => {
      const fieldName = 'DefaultFields';
      addQuery({
        fieldName: `<${fieldName}>`,
        fieldLabel: t('queryBuilder.defaultFields'),
        valueLabel: value,
        values: {
          value,
          id: `${uuidv4()}__${QBDropTypes.DEFAULT_FIELD}__${new Date().valueOf()}__${fieldName}`,
          ruleLabel: t('queryBuilder.defaultFields'),
          fieldType: 'default_field',
          valueLabel: value,
        },
        extraData: {
          operator: QBOperators.contains,
          fieldType: 'default_field',
        },
      });
    };

    const handleMetadataApply = (
      value: IQueryFieldMetadataDrawerValue,
      type: string
    ) => {
      if (value.field?.fieldLabel) {
        const fieldValue = value.fieldValue?.fieldValue;
        const fieldUIType =
          FieldUITypeMappingObj[value.field.fieldTypeId as FieldUIType];
        const operator = value.fieldValue?.extraValue?.operator as QBOperators;
        addQuery({
          fieldName: `<${value.field.fieldLabel}>`,
          fieldLabel: value.field.fieldLabel,
          values: {
            hasTransferList: value?.field?.hasTransferList ?? false,
            value: fieldValue,
            id: `${uuidv4()}__${type}__${new Date().valueOf()}__${value.field.fieldId}`,
            ruleLabel: value.field.fieldLabel,
            fieldType: fieldUIType,
          },
          extraData: {
            operator,
            fieldType: fieldUIType,
          },
        });
        const hasTransferList = value?.field?.hasTransferList ?? false;
        if (hasTransferList) {
          dispatch(
            setHasTransferListById({
              id: `${value?.field?.fieldId}`,
              value: hasTransferList,
            })
          );
        }
      }
    };

    const handleSavedSearchSelection = (value: SavedSearchType) => {
      setIsSavedSearchDialogVisible(false);
      addQuery({
        fieldName: '<SavedSearch>',
        fieldLabel: t('queryBuilder.savedSearch'),
        valueLabel: value.label,
        values: {
          value: value.ecaSearchId,
          id: `${uuidv4()}__${QBDropTypes.SAVED_SEARCH}__${new Date().valueOf()}__${value.id}`,
          ruleLabel: t('queryBuilder.savedSearch'),
          fieldType: 'saved_search',
          valueLabel: value.label,
        },
        extraData: {
          operator: QBOperators.isIn,
          fieldType: 'saved_search',
        },
      });
      if (value.directory_structure_ids) {
        dispatch(
          setSavedSearchById({
            id: value.id,
            value: value.directory_structure_ids,
          })
        );
      }

      if (!isEmpty(value?.relationalGroupList ?? [])) {
        dispatch(
          setSavedSearchRelationalGroupById({
            id: value.id,
            value: value?.relationalGroupList ?? [],
          })
        );
      }
    };

    const addRelationalGroup = () => {
      onPressAddRelationshipGroup?.();
    };

    const addRuleGroup = () => {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.queryBuilder.logicGroup'),
          type: GA_CLICK_TYPES.BUTTON,
          region: GA_REGIONS.BODY,
          action: t('gaTracking.queryBuilder.logicGroupAction'),
        },
      });

      const newGroup: RuleGroupTypeIC = {
        id: uuidv4(),
        rules: [],
      };

      // Update the query builder object to include the new group
      const updatedQuery = {
        ...builder,
        rules: builder.rules.length
          ? [...builder.rules, QBCombinators.AND, newGroup]
          : [...builder.rules, newGroup], // Add new rule to the existing rules array
      } as RuleGroupTypeIC;

      // Dispatch the updated query to the store
      dispatch(updateQuery(updatedQuery));
    };

    const handleScrollToTop = () => {
      const queryBuilderContainer = document.getElementsByClassName(
        'aur-query-builder'
      )?.[0] as HTMLElement | null;

      const initialQueryHeaderTop = queryBuilderContainer?.offsetTop ?? 0;

      document.querySelector('.queryBuilder')?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
      window.scrollTo({
        top: initialQueryHeaderTop,
        left: 0,
        behavior: 'smooth',
      });
    };

    const trackComponentOnDrag = (componentName: string) => {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: componentName,
          type: GA_CLICK_TYPES.DRAG_DROP,
          region: GA_REGIONS.BODY,
          action: t('gaTracking.queryBuilder.applyAction'),
        },
      });
    };

    return (
      <>
        <QueryBuilderDnD
          dnd={{...ReactDnD, ...ReactDndHtml5Backend}}
          controlElements={controlElements} // Pass controlElements with the custom rule
        >
          <Box ref={drop}>
            <div>
              <div className="qb-header-button-container">
                <div className="qb-header-container">
                  {builder?.rules?.length > 0 ? (
                    <span className="qb-query-area">
                      <Typography
                        noWrap
                        className={'query-text typo-text label1_size'}
                      >
                        {`${t('queryBuilder.query')} ${processRules(builder.rules)}`}
                      </Typography>
                    </span>
                  ) : (
                    <div></div>
                  )}
                  <div className="qb-header-area">
                    <AurButton
                      classNames="chip-reset-button-icon"
                      type="text"
                      onClick={addRuleGroup}
                      startIcon={
                        <AurIcon
                          icon={IconNames.LogicIcon}
                          customClassName={'add-logic-icon'}
                        />
                      }
                      label={t('queryBuilder.logicGroup')}
                      size="Small"
                    />
                    {builder?.rules?.length > 0 && (
                      <>
                        <AurButton
                          classNames="chip-reset-button-icon"
                          type="text"
                          startIcon={<AddIcon width={'16px'} height={'16px'} aria-hidden="true" />}
                          size="Small"
                          onClick={addRelationalGroup}
                          dataTestId="Reset to Default"
                        >
                          {t('queryBuilder.addRelationalGroup')}
                        </AurButton>
                        <AurButton
                          classNames="chip-reset-button-icon"
                          type="text"
                          startIcon={
                            <SyntaxIcon width={'16px'} height={'16px'} aria-hidden="true" />
                          }
                          size="Small"
                          disabled={!builder.rules.length}
                          onClick={() => {
                            setViewSyntax(true);
                            trackGAEvent({
                              eventName: GA_EVENT_NAMES.CLICK,
                              click: {
                                name: t('gaTracking.queryBuilder.viewSyntax'),
                                type: GA_CLICK_TYPES.BUTTON,
                                region: GA_REGIONS.BODY,
                                action: t(
                                  'gaTracking.queryBuilder.viewSyntaxAction'
                                ),
                              },
                            });
                          }}
                          dataTestId="Reset to Default"
                        >
                          {t('queryBuilder.viewSyntax')}
                        </AurButton>
                      </>
                    )}
                  </div>
                </div>
                {builder.rules.length > 0 &&
                  !isEmpty(selectedRelationshipGroupArray) && (
                    <span className="rgp-text">
                      <Box
                        className={
                          'aurAdhocSearch-relationshipGroup-button-container'
                        }
                      >
                        <Typography
                          component="span"
                          className="aurAdhocSearch-relationshipGroup-text"
                        >
                          {t('queryBuilder.RGFs')}
                        </Typography>
                        <Button
                          data-testid={'addRelationshipButtonWithGroup'}
                          className={'aurAdhocSearch-relationshipGroup-button'}
                          onClick={addRelationalGroup}
                          size={'medium'}
                        >
                          <Typography
                            ref={typographyRef}
                            data-testid={'addRelationshipGroupText'}
                            noWrap
                            className={
                              'typo-text buttonMedium_size aurAdhocSearch-relationshipGroup-buttonText'
                            }
                          >
                            {selectedRelationshipGroupText}
                          </Typography>
                          {hasRelationshipGroupMoreCount ? (
                            <Box
                              className={
                                'aurAdhocSearch-relationshipGroup-moreText-container'
                              }
                            >
                              <Typography
                                className={
                                  'typo-text buttonMedium_size aurAdhocSearch-relationshipGroup-moreText'
                                }
                              >
                                {`+${selectedRelationshipGroupArray.length} ${t('indexSearchPage.aurAdhocSearchControl.more')}`}
                              </Typography>
                            </Box>
                          ) : null}
                        </Button>
                      </Box>
                    </span>
                  )}
              </div>
              <Box className="query-builder-container">
                {builder.rules.length == 0 ? (
                  <Box className="empty-query-builder">
                    <Typography className="typo-text subhead3_size mist-100">
                      {t('queryBuilder.dropBoxPlaceholder')}
                    </Typography>
                  </Box>
                ) : (
                  <QueryBuilder
                    fields={fields}
                    query={builder}
                    onQueryChange={(updatedQuery: RuleGroupTypeIC) => {
                      dispatch(updateQuery(updatedQuery));
                    }}
                    independentCombinators
                    showCombinatorsBetweenRules
                  />
                )}
                {builder.rules.length > 2 && !isAtTop ? (
                  <Box
                    onClick={handleScrollToTop}
                    className="scroll-to-top"
                    role="button"
                  >
                    <AurIcon icon={IconNames.ArrowUpwardIcon} />
                    <Typography className="typo-text buttonMedium_size">
                      {t('queryBuilder.backToTop')}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            </div>
          </Box>
        </QueryBuilderDnD>
        {viewSyntax ? (
          <ViewSyntaxDrawer open={viewSyntax} setViewSyntax={setViewSyntax} />
        ) : null}
        {isShowTagPanel && (
          <QueryFieldTagDrawer
            open={isShowTagPanel}
            defaultSelectedTagIds={initialEditValue?.fieldId}
            onCancel={() => {
              setIsShowTagPanel(false);
              clearEditField();
            }}
            onApply={(tags) => {
              setIsShowTagPanel(false);
              const ids = tags?.map((item) => item.id).join('##') ?? '';
              let labels = tags?.map((item) => item.label).join(' OR ') ?? '';
              let name = tags?.[0]?.parentFolder;
              const field = tags?.[0];
              if (field?.fileType === 'folder') {
                name = labels;
                labels = '*';
              }

              addQuery({
                fieldName: `<${name}>`,
                fieldLabel: name || '-',
                valueLabel: labels,
                values: {
                  value: labels,
                  id: `${uuidv4()}__${QBDropTypes.TAG}__${new Date().valueOf()}__${ids}`,
                  ruleLabel: labels,
                  fieldType: 'tag',
                  valueLabel: labels,
                },
                extraData: {
                  operator: QBOperators.isIn,
                  fieldType: 'tag',
                },
              });
            }}
          />
        )}
        {isShowExportPanel && (
          <QueryFieldExportDrawer
            open={isShowExportPanel}
            defaultSelectedExportId={initialEditValue?.fieldId}
            onCancel={() => {
              setIsShowExportPanel(false);
              clearEditField();
            }}
            onApply={(exportValue) => {
              setIsShowExportPanel(false);
              addQuery({
                fieldName: `<ExportedToReview>`,
                fieldLabel: t('queryBuilder.exports'),
                values: {
                  value: exportValue?.exportName,
                  id: `${uuidv4()}__${QBDropTypes.EXPORT}__${new Date().valueOf()}__${exportValue?.exportId}`,
                  ruleLabel: t('queryBuilder.exports'),
                  fieldType: 'export',
                },
                extraData: {
                  operator: QBOperators.isIn,
                  fieldType: 'export',
                },
              });
            }}
          />
        )}
        {isShowDefaultFieldPanel && (
          <QueryFieldDefaultDrawer
            open={isShowDefaultFieldPanel}
            initialInputValue={initialEditValue?.value ?? ''}
            onCancel={() => {
              setIsShowDefaultFieldPanel(false);
              clearEditField();
            }}
            onApply={(value) => {
              setIsShowDefaultFieldPanel(false);
              handleDefaultFieldApply(value);
            }}
          />
        )}
        {isShowStandardMetadataPanel && (
          <QueryFieldMetadataDrawer
            type="STANDARD_METADATA"
            open={isShowStandardMetadataPanel}
            initialValue={
              (initialEditValue?.fieldId
                ? {
                    ...initialEditValue,
                    fieldId: parseInt(initialEditValue.fieldId),
                  }
                : undefined) as never
            }
            onCancel={() => {
              setIsShowStandardMetadataPanel(false);
              clearEditField();
            }}
            onApply={(value) => {
              setIsShowStandardMetadataPanel(false);
              handleMetadataApply(value, QBDropTypes.STANDARED_METADATA_FIELD);
            }}
          />
        )}
        {isShowCustomMetadataPanel && (
          <QueryFieldMetadataDrawer
            type="CUSTOM_METADATA"
            open={isShowCustomMetadataPanel}
            initialValue={
              (initialEditValue?.fieldId
                ? {
                    ...initialEditValue,
                    fieldId: parseInt(initialEditValue.fieldId),
                  }
                : undefined) as never
            }
            onCancel={() => {
              setIsShowCustomMetadataPanel(false);
              clearEditField();
            }}
            onApply={(value) => {
              setIsShowCustomMetadataPanel(false);
              handleMetadataApply(value, QBDropTypes.CUSTOM_METADATA_FIELD);
            }}
          />
        )}
        {isSavedSearchDialogVisible && (
          <ECASavedSearchPanelSideDrawer
            title={t('indexSearchPage.savedSearchDrawer.title')}
            searchPlaceholder={t('indexSearchPage.savedSearchDrawer.search')}
            indexNameLabel={t('indexSearchPage.savedSearchDrawer.index')}
            showQueryFieldHeader
            showDrawer={isSavedSearchDialogVisible}
            onCloseDrawer={() => {
              setIsSavedSearchDialogVisible(false);
              clearEditField();
            }}
            onSelectSavedSearch={handleSavedSearchSelection}
            showNotes={false}
            isUsedInQueryBuilder
            expandedIds={
              editField?.saved_search_path
                ? editField.saved_search_path.split('/')
                : undefined
            }
            selectedSearch={
              initialEditValue?.fieldId
                ? ({id: initialEditValue.fieldId} as never)
                : undefined
            }
          />
        )}
      </>
    );
  }
);

export default QueryBuilderComponent;
